import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DownArrow from "../../assets/svgs/down-arrow.svg";
import Plus from "../../assets/svgs/Plus.svg";
import sortAsce from "../../assets/svgs/sort-ascending.svg";
import sortDesc from "../../assets/svgs/sort-descending.svg";
import swap from "../../assets/svgs/swap-vertical.svg";
import UpArrow from "../../assets/svgs/up-arrow.svg";
import ActionsMenu from "../../components/ActionsMenu";
import BarData from "../../components/Charts/BarData";
import CircleChart from "../../components/Charts/CircleChart";
import ConfirmationModal from "../../components/ConfirmationModals/ConfirmationModal";
import ContractModal from "../../components/ConfirmationModals/ContractModal";
import DeleteConfirmationModal from "../../components/ConfirmationModals/DeleteConfirmationModal";
import Loader from "../../components/Loader";
import ContactPagination from "../../components/Pagination/ContactPagination";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import LeadDataContainer from "../../pages/Leads/components/LeadDataContainer";
import { BASE_URL } from "../../utils/Element";
import { initialExecutedFilterData } from "../../utils/initialData";
import EditExecutedModal from "./component/EditExecutedModal";
import ExecutedAddModal from "./component/ExecutedAddModal";
import ExecutedFilter from "./component/ExecutedFilter";

// Custom debounce hook
const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const ExecutedLOI = () => {
  const [config] = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [leadsData, setLeadsData] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState({});
  const [itemPerPage, setItemPerPage] = useState(50);
  const [sortBy, setSortBy] = useState("id");
  const [sortDirection, setSortDirection] = useState("asc");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [disableDelete, setDisableDelete] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [excecutedDashboard, setExcecutedDashboard] = useState();
  const [showContractModal, setShowContractModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed);
  const [paginationData, setPaginationData] = useState({ totalItems: 0, from: 0, to: 0, totalPages: 0 });
  const queryParams = new URLSearchParams(location.search);

  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData");
    return localFilterData ? JSON.parse(localFilterData) : initialExecutedFilterData;
  });

  const [filterStatus, setFilterStatus] = useState({
    avg_commission_per_transaction_ytd: false,
    executedlo_buyer: false,
    executedlo_seller: false,
    executedloi_gcl_to_company_ytd: false,
    executedloi_volume_ytd: false,
    executedloi_ytd: false,
    new_executedloi_quarter: false,
  });

  // ==================== Debounced Search ====================
  const [searchInput, setSearchInput] = useState(queryParams.get('search') ?? '');
  const debouncedSearch = useDebounce(searchInput, 600);
  const [search, setSearch] = useState(queryParams.get('search') ?? '');

  // Trigger API only when >= 3 chars or cleared
  useEffect(() => {
    if (debouncedSearch.length >= 3 || debouncedSearch === '') {
      setSearch(debouncedSearch);
    }
  }, [debouncedSearch]);

  // URL sync with debounced search
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (search.trim()) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    navigate({ search: params.toString() }, { replace: true });
  }, [search, navigate, location.search]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const trimmed = newValue.startsWith(" ") ? newValue.trimStart() : newValue;
    setSearchInput(trimmed);
  };

  // ==================== Existing Logic ====================
  const handleNext = () => {
    if (currentPage !== paginationData.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const buildQueryParams = (filters) => {
    let params = `&page=${currentPage}&per_page=${itemPerPage}&search=${search}`;

    const {
      created_at, property_name, noi, created_end_date, created_start_date, min_contract_price, max_contract_price, min_contract_cap_rate, max_contract_cap_rate, max_asking_price, min_asking_price, max_potential_gcl, avg_commission_per_transaction_ytd, executedlo_buyer,
      executedlo_seller, executedloi_gcl_to_company_ytd, executedloi_volume_ytd, executedloi_ytd, new_executedloi_quarter, jan, feb, mar, apr, may, jun, jul, aug, sep, oct, nov, dec } = filters;

    if (created_at) {
      params += `&created_at=${encodeURIComponent(created_at)}`;
    }
    if (created_start_date) {
      params += `&created_start_date=${encodeURIComponent(created_start_date)}`;
    }
    if (created_end_date) {
      params += `&created_end_date=${encodeURIComponent(created_end_date)}`;
    }
    if (property_name) {
      params += `&property_name=${encodeURIComponent(property_name)}`;
    }
    if (noi) {
      params += `&noi=${encodeURIComponent(noi)}`;
    }
    if (min_contract_price) {
      params += `&min_contract_price=${encodeURIComponent(min_contract_price)}`;
    }
    if (max_contract_price) {
      params += `&max_contract_price=${encodeURIComponent(max_contract_price)}`;
    }
    if (min_contract_cap_rate) {
      params += `&min_contract_cap_rate=${encodeURIComponent(min_contract_cap_rate)}`;
    }
    if (max_contract_cap_rate) {
      params += `&max_contract_cap_rate=${encodeURIComponent(max_contract_cap_rate)}`;
    }
    if (min_asking_price) {
      params += `&min_asking_price=${encodeURIComponent(min_asking_price)}`;
    }
    if (max_asking_price) {
      params += `&max_asking_price=${encodeURIComponent(max_asking_price)}`;
    }
    if (avg_commission_per_transaction_ytd) {
      params += `&avg_commission_per_transaction_ytd=true`;
    }
    if (executedlo_buyer) {
      params += `&executedlo_buyer=true`;
    }
    if (executedlo_seller) {
      params += `&executedlo_seller=true`;
    }
    if (max_potential_gcl) {
      params += `&max_potential_gcl=true`;
    }
    if (executedloi_gcl_to_company_ytd) {
      params += `&executedloi_gcl_to_company_ytd=true`;
    }
    if (executedloi_volume_ytd) {
      params += `&executedloi_volume_ytd=true`;
    }
    if (executedloi_ytd) {
      params += `&executedloi_ytd=true`;
    }
    if (jan) {
      params += `&jan=true`;
    }
    if (feb) {
      params += `&feb=true`;
    }
    if (mar) {
      params += `&mar=true`;
    }
    if (apr) {
      params += `&apr=true`;
    }
    if (may) {
      params += `&may=true`;
    }
    if (jun) {
      params += `&jun=true`;
    }
    if (jul) {
      params += `&jul=true`;
    }
    if (aug) {
      params += `&aug=true`;
    }
    if (sep) {
      params += `&sep=true`;
    }
    if (oct) {
      params += `&oct=true`;
    }
    if (nov) {
      params += `&nov=true`;
    }
    if (dec) {
      params += `&dec=true`;
    }
    if (new_executedloi_quarter) {
      params += `&new_executedloi_quarter=true`;
    }
    if (sortDirection && sortBy !== "id") {
      params += `&sort_direction=${sortDirection}`;
    }
    if (sortBy !== "id" && sortBy !== undefined) {
      params += `&sort_by=${sortBy}`;
    }

    return params;
  };

  const fetchExecuted = (filters) => {
    const queryParams = buildQueryParams({
      ...filters,
      avg_commission_per_transaction_ytd: filterStatus.avg_commission_per_transaction_ytd,
      executedlo_buyer: filterStatus.executedlo_buyer,
      executedlo_seller: filterStatus.executedlo_seller,
      executedloi_gcl_to_company_ytd: filterStatus.executedloi_gcl_to_company_ytd,
      executedloi_volume_ytd: filterStatus.executedloi_volume_ytd,
      executedloi_ytd: filterStatus.executedloi_ytd,
      new_executedloi_quarter: filterStatus.new_executedloi_quarter,
      jan: filterStatus.jan,
      feb: filterStatus.feb,
      mar: filterStatus.mar,
      apr: filterStatus.apr,
      may: filterStatus.may,
      jun: filterStatus.jun,
      jul: filterStatus.jul,
      aug: filterStatus.aug,
      sep: filterStatus.sep,
      oct: filterStatus.oct,
      nov: filterStatus.nov,
      dec: filterStatus.dec,
    });
    axios
      .get(`${BASE_URL}/executeds?avtive_tab=${activeTab}${queryParams}`, config)
      .then((res) => {
        const value = res?.data?.executed;
        setLeadsData(value?.data || []);
        setPaginationData({
          totalItems: value?.total ?? 0,
          from: value?.from ?? 0,
          to: value?.to ?? 0,
          totalPages: value?.last_page ?? 0,
        });
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  const handleUnderContract = () => {
    axios
      .delete(`${BASE_URL}/mark-as-under-contract/${selectedId}`, config)
      .then((res) => {
        setSelectedId("");
        onSuccess();
        setShowContractModal(false);
        if (res?.data?.message) {
          NotificationManager.success(res?.data?.message);
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchExecuted(filterData);
  }, [sortDirection, sortBy]);

  useEffect(() => {
    onSuccess();
  }, [search, currentPage, itemPerPage, activeTab]);

  const onSuccess = (filters) => {
    setLoading(true);
    if (filters) {
      fetchExecuted(filters);
    } else {
      fetchExecuted(filterData);
    }
  };

  const handleDelete = () => {
    setDisableDelete(true);
    axios
      .delete(`${BASE_URL}/delete-executed/${selectedId}`, config)
      .then((res) => {
        setSelectedId("");
        onSuccess();
        setShowDeleteModal(false);
        if (res?.data?.message) {
          NotificationManager.success(res?.data?.message);
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setDisableDelete(false));
  };

  const data = excecutedDashboard?.day_graph;

  const BarGraphData = [
    { name: "Jan", count: excecutedDashboard?.Jan, key: "jan" },
    { name: "Feb", count: excecutedDashboard?.Feb, key: "feb" },
    { name: "Mar", count: excecutedDashboard?.Mar, key: "mar" },
    { name: "Apr", count: excecutedDashboard?.Apr, key: "apr" },
    { name: "May", count: excecutedDashboard?.May, key: "may" },
    { name: "Jun", count: excecutedDashboard?.Jun, key: "jun" },
    { name: "Jul", count: excecutedDashboard?.Jul, key: "jul" },
    { name: "Aug", count: excecutedDashboard?.Aug, key: "aug" },
    { name: "Sep", count: excecutedDashboard?.Sept, key: "sep" },
    { name: "Oct", count: excecutedDashboard?.Oct, key: "oct" },
    { name: "Nov", count: excecutedDashboard?.Nov, key: "nov" },
    { name: "Dec", count: excecutedDashboard?.Dec, key: "dec" },
  ];

  const toggleRowDetails = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const fetchDashboardData = () => {
    setLoading(true);

    axios
      .get(`${BASE_URL}/dashboard-executedloi?active_tab=${activeTab}`, config)
      .then((res) => {
        setExcecutedDashboard(res?.data?.executedloi_dashboard);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDashboardData();
  }, [activeTab]);

  const handleBoxClick = (nameOrKey) => {
    const filterKey = nameOrKey;
    let targetTab = activeTab;
    if (activeTab === 0) {
      targetTab = 1;
    } else if (activeTab === 2) {
      targetTab = 3;
    }

    setActiveTab(targetTab);
    setCurrentPage(1);

    setFilterStatus({
      avg_commission_per_transaction_ytd: filterKey === "avg_commission_per_transaction_ytd",
      executedlo_buyer: filterKey === "executedlo_buyer",
      executedlo_seller: filterKey === "executedlo_seller",
      executedloi_gcl_to_company_ytd: filterKey === "executedloi_gcl_to_company_ytd",
      executedloi_volume_ytd: filterKey === "executedloi_volume_ytd",
      executedloi_ytd: filterKey === "executedloi_ytd",
      new_executedloi_quarter: filterKey === "new_executedloi_quarter",
      jan: filterKey === "jan",
      feb: filterKey === "feb",
      mar: filterKey === "mar",
      apr: filterKey === "apr",
      may: filterKey === "may",
      jun: filterKey === "jun",
      jul: filterKey === "jul",
      aug: filterKey === "aug",
      sep: filterKey === "sep",
      oct: filterKey === "oct",
      nov: filterKey === "nov",
      dec: filterKey === "dec",
    });
  };

  return (
    <BaseLayout>
      <div className="contact-header">
        <p className="head-1 dark-H">Executed LOI</p>
        <div className="md:flex block gap-4 items-center">
          <div className="flex gap-4 justify-between items-center">
            <ExecutedFilter
              filterData={filterData}
              onSetFilterData={(value) => {
                setFilterData(value);
              }}
              from="Executed LOI"
              onCallApiAgain={(filters) => onSuccess(filters)}
            />
            {/* Updated Search Box */}
            <div className="search-box contacts relative">
              <input
                type="text"
                className="body-S"
                placeholder="Search contact name"
                value={searchInput}
                onChange={handleInputChange}
                style={{ border: "1px solid #D8D8D8", marginTop: "0px" }}
              />
            </div>
            <button onClick={() => setShowAddModal(true)} className="add-contact-button green-bg-H light-L body-S">
              <img className="mr-2" src={Plus || "/placeholder.svg"} alt="plus" /> Add
            </button>
          </div>
        </div>
      </div>

      <div className="task-tabs mx-1 mt-2 flex justify-between body-L dark-M mb-4">
        <div className="flex gap-4">
          {["My Dashboard", "My Executed LOI", "Team Dashboard", "Team Executed LOI"].map((tab, index) => (
            <p
              key={index}
              role="button"
              className={`${activeTab === index ? "head-4 dark-H active" : ""} py-1`}
              onClick={() => {
                setActiveTab(index);
                setCurrentPage(1);
                setFilterStatus({
                  avg_commission_per_transaction_ytd: false,
                  executedlo_buyer: false,
                  executedlo_seller: false,
                  executedloi_gcl_to_company_ytd: false,
                  executedloi_volume_ytd: false,
                  executedloi_ytd: false,
                  new_executedloi_quarter: false,
                });
              }}
            >
              {tab}
            </p>
          ))}
        </div>
      </div>

      {(activeTab === 1 || activeTab === 3) && (
        <div className="light-bg-L pb-3 table-container">
          <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
            {loading ? (
              <Loader />
            ) : leadsData?.length === 0 ? (
              <p className="body-N text-center mt-6 p-10">No Pipline Available</p>
            ) : (
              <table className="contact-table light-bg-L">
                <thead>
                  <tr className="uppercase body-N dark-M">
                    <th>
                      <p>Details</p>
                    </th>

                    <th className="green-H">
                      <div className="table-header">
                        EXECUTED LOI DATE
                        <img
                          role="button"
                          src={sortBy !== "created_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => {
                            setSortBy("created_at");
                            if (sortDirection === "desc") {
                              setSortDirection("asc");
                            } else {
                              setSortDirection("desc");
                            }
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        PROPERTY NAME
                        <img
                          role="button"
                          src={sortBy !== "broker" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("broker");
                            if (sortDirection === "desc") {
                              setSortDirection("asc");
                            } else {
                              setSortDirection("desc");
                            }
                          }}
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        NOI
                        <img
                          role="button"
                          src={sortBy !== "created_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("created_at");
                            if (sortDirection === "desc") {
                              setSortDirection("asc");
                            } else {
                              setSortDirection("desc");
                            }
                          }}
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        CONTRACT PRICE
                        <img
                          role="button"
                          src={sortBy !== "contract_price" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("contract_price");
                            if (sortDirection === "desc") {
                              setSortDirection("asc");
                            } else {
                              setSortDirection("desc");
                            }
                          }}
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        CONTRACT CAP RATE
                        <img
                          role="button"
                          src={sortBy !== "contract_cap_rate" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("contract_cap_rate");
                            if (sortDirection === "desc") {
                              setSortDirection("asc");
                            } else {
                              setSortDirection("desc");
                            }
                          }}
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        % OF ASKING PRICE
                        <img
                          role="button"
                          src={sortBy !== "asking_price" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("asking_price");
                            if (sortDirection === "desc") {
                              setSortDirection("asc");
                            } else {
                              setSortDirection("desc");
                            }
                          }}
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        GCI TO COMPANY
                        <img
                          role="button"
                          src={sortBy !== "company_gcl" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("company_gcl");
                            if (sortDirection === "desc") {
                              setSortDirection("asc");
                            } else {
                              setSortDirection("desc");
                            }
                          }}
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">Actions</div>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {leadsData?.map((el, idx) => (
                    <React.Fragment key={idx}>
                      <tr className="dark-H body-N">
                        <td>
                          <img
                            role="button"
                            src={expandedRows[el.id] ? UpArrow : DownArrow}
                            alt="icon"
                            className="sidebar-icons"
                            onClick={() => toggleRowDetails(el.id)}
                          />
                        </td>
                        <td>{moment(el?.created_at).tz('America/New_York').format("MM/DD/YY")}</td>

                        <td className="head-5 capitalize">
                          <Link to={
                            el?.portfolio === 0
                              ? `/property/${el?.property_id}`
                              : el?.link?.owner_info_type === "contact"
                                ? `/contact/${el?.link?.owner?.id}`
                                : `/company/${el?.link?.owner?.id}`
                          }
                            onClick={(e) => {
                              e.preventDefault();

                              if (el?.portfolio === 0) {
                                navigate(`/property/${el?.property_id}`);
                              } else if (el?.link?.owner_info_type === "contact") {
                                navigate(`/contact/${el?.link?.owner?.id}`);
                              } else if (el?.link?.owner_info_type === "company") {
                                navigate(`/company/${el?.link?.owner?.id}`);
                              }
                            }}>
                            {el?.link?.property_name} <br />
                            {el?.link?.street_address} <br />
                            {el?.link?.state && el?.link?.city ? ` ${el?.link?.city} ,  ${el?.link?.state} ` : ""}
                          </Link>
                        </td>
                        <td >${el?.link?.noi_format}</td>
                        <td className="text-center">${el?.contract_price_format}</td>
                        <td className="text-center">
                          <p className="capitalize">{el?.contract_cap_rate_format}%</p>
                        </td>
                        <td className="text-center">
                          {el?.asking_price_format_percentage}%
                        </td>
                        <td className="text-center">${el?.gross_commission?.gci_to_company}</td>
                        <td className="flex gap-3 items-center w-full justify-center">
                          <ActionsMenu
                            from="ExecutedLoi"
                            handleEdit={() => {
                              setShowEditModal(true);
                              setSelectedId(el?.id)
                            }}
                            handleDelete={() => {
                              setSelectedId(el?.id);
                              setShowDeleteModal(true);
                            }}
                            showOtherOption={true}
                            otherOptionTitle={"New Counter Offer"}
                            handleOtherOption={() => {
                              setShowAddModal(true);
                              setSelectedId(el?.id);
                            }}
                            otherUnderContractTitle={<> Convert to <br /> Under Contract</>}
                            handleUnderContract={() => {
                              setSelectedId(el?.id);
                              setShowContractModal(true);
                            }}

                          />
                        </td>
                      </tr>

                      {expandedRows[el.id] && (
                        <tr>
                          <td colSpan="11">
                            <div className="expanded-row-content p-4 bg-[rgb(244,244,244)] rounded-md">
                              <div className="">

                                {/* --- TOP INFO SECTION --- */}
                                <div className="grid grid-cols-5 gap-6">

                                  <div>
                                    <p className="dark-H head-4 mb-2">Acquisition</p>
                                    <Link
                                      to={
                                        el?.offer?.acquistion_name_id?.type === "contact"
                                          ? `/contact/${el?.offer?.acquistion_name_id?.id}`
                                          : el?.offer?.acquistion_name_id?.type === "property"
                                            ? `/property/${el?.offer?.acquistion_name_id?.id}`
                                            : "#"
                                      }
                                      className="body-N dark-H"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        const acquisition = el?.offer?.acquistion_name_id;
                                        if (acquisition?.type === "contact") {
                                          navigate(`/contact/${acquisition?.id}`);
                                        } else if (acquisition?.type === "property") {
                                          navigate(`/property/${acquisition?.id}`);
                                        }
                                      }}
                                    >
                                      {el?.offer?.acquistion_name || ""}
                                    </Link>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Disposition</p>
                                    <Link
                                      to={
                                        el?.offer?.disposition_name_id?.type === "contact"
                                          ? `/contact/${el?.offer?.disposition_name_id?.id}`
                                          : el?.offer?.disposition_name_id?.type === "property"
                                            ? `/property/${el?.offer?.disposition_name_id?.id}`
                                            : "#"
                                      }
                                      className="body-N dark-H"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        const disposition = el?.offer?.disposition_name_id;
                                        if (disposition?.type === "contact") {
                                          navigate(`/contact/${disposition?.id}`);
                                        } else if (disposition?.type === "property") {
                                          navigate(`/property/${disposition?.id}`);
                                        }
                                      }}
                                    >
                                      {el?.offer?.disposition_name || ""}
                                    </Link>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller Broker</p>
                                    <p className="body-N dark-H">
                                      {el?.offer?.seller_broker?.length > 0
                                        ? el.offer.seller_broker.map((broker, index) => (
                                          <span key={broker.id || index}>
                                            {broker?.first_name || ""} {broker?.last_name || ""}
                                            {index < el.offer.seller_broker.length - 1 ? ", " : ""}
                                          </span>
                                        ))
                                        : ""}
                                    </p>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller BDS</p>
                                    <p className="body-N dark-H">
                                      {el?.offer?.seller_bds?.first_name} {el?.offer?.seller_bds?.last_name || ""}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer Broker</p>
                                    <p className="body-N dark-H">
                                      {el?.offer?.buyer_broker?.length > 0
                                        ? el.offer.buyer_broker.map((broker, index) => (
                                          <span key={broker.id || index}>
                                            {broker?.first_name || ""} {broker?.last_name || ""}
                                            {index < el.offer.buyer_broker.length - 1 && <br />}
                                          </span>
                                        ))
                                        : ""}
                                    </p>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer BDS</p>
                                    <p className="body-N dark-H">
                                      {el?.offer?.buyer_bds?.first_name} {el?.offer?.buyer_bds?.last_name || ""}
                                    </p>
                                  </div>

                                </div>

                                {/* --- GROSS COMMISSION SECTION --- */}
                                <p className="head-3 dark-H my-6 text-center">GROSS COMMISSION INCOME BREAKDOWN</p>

                                <div className="grid grid-cols-4 gap-6">
                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller Broker</p>
                                    <div>
                                      <p className="body-N dark-H">
                                        ${el?.gross_commission?.gci_to_broker_seller?.toLocaleString() || 0}
                                      </p>
                                      {el?.gcl_broker_seller_share?.length > 0 && (
                                        <div className="mt-1">
                                          {el.gcl_broker_seller_share.map((item, index) => (
                                            <p key={index} className="body-N dark-H">
                                              {item.name} ({item.share})
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer Broker</p>
                                    <div>
                                      <p className="body-N dark-H">
                                        ${el?.gross_commission?.gci_to_broker_buyer?.toLocaleString() || 0}
                                      </p>
                                      {el?.gcl_broker_buyer_share?.length > 0 && (
                                        <div className="mt-1">
                                          {el.gcl_broker_buyer_share.map((item, index) => (
                                            <p key={index} className="body-N dark-H">
                                              {item.name} ({item.share})
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>





                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller BDS</p>
                                    <p className="body-N dark-H">
                                      {el?.gross_commission?.gci_to_broker_representing_seller
                                        ? `$${el.gross_commission?.gci_to_broker_representing_seller.toLocaleString()}`
                                        : "$0"}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer BDS</p>
                                    <p className="body-N dark-H">
                                      {el?.gross_commission?.gci_to_broker_representing_buyer
                                        ? `$${el.gross_commission?.gci_to_broker_representing_buyer.toLocaleString()}`
                                        : "$0"}
                                    </p>
                                  </div>
                                </div>

                              </div>
                            </div>
                          </td>
                        </tr>
                      )}

                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {leadsData?.length > 0 && (
            <ContactPagination
              activeTab={activeTab}
              paginationData={paginationData}
              handleNext={handleNext}
              handlePrev={handlePrev}
              onSuccess={onSuccess}
              itemPerPage={itemPerPage}
              handleItemPerPageSelect={(value) => {
                setItemPerPage(value);
                setCurrentPage(1);
              }}
            />
          )}
        </div>
      )}

      {(activeTab === 0 || activeTab === 2) && (
        <>
          <div className="">
            <div className="w-auto">
              <div className="flex items-center gap-3">
                <LeadDataContainer text="NEW EXECUTED LOI THIS QUARTER"
                  number={excecutedDashboard?.new_executedloi_quarter}
                  onClick={() => handleBoxClick("new_executedloi_quarter")}
                  className="flex-1" />

                <LeadDataContainer text="TOTAL CURRENT EXECUTED LOI YTD "
                  number={excecutedDashboard?.executedloi_ytd}
                  className="flex-1"
                  onClick={() => handleBoxClick("executedloi_ytd")}
                />

                <LeadDataContainer text="EXECUTED LOI TOTAL VOLUME YTD" number={
                  excecutedDashboard?.executedloi_volume_ytd
                    ? `$${excecutedDashboard.executedloi_volume_ytd}`
                    : '0'
                } className="flex-1"
                  onClick={() => handleBoxClick("executedloi_volume_ytd")}
                />

                <LeadDataContainer text="CURRENT POTENTIAL GCI TO COMPANY YTD" number={
                  excecutedDashboard?.executedloi_gcl_to_company_ytd
                    ? `$${excecutedDashboard.executedloi_gcl_to_company_ytd}`
                    : '0'
                }
                  className="flex-1"
                  onClick={() => handleBoxClick("executedloi_gcl_to_company_ytd")} />

                <LeadDataContainer text="AVG. COMMISSION PER TRANSACTION YTD" number={
                  excecutedDashboard?.avg_commission_per_transaction_ytd
                    ? `$${excecutedDashboard.avg_commission_per_transaction_ytd}`
                    : '0'
                }
                  className="flex-1"
                  onClick={() => handleBoxClick("avg_commission_per_transaction_ytd")} />

              </div>

              <div className="flex gap-3 ">

                <div className="w-[25%]">
                  <LeadDataContainer text="EXECUTED LOI BUYER TRANSACTIONS YTD"
                    number={excecutedDashboard?.executedlo_buyer}
                    className="flex-1"
                    onClick={() => handleBoxClick("executedlo_buyer")}
                  />
                  <LeadDataContainer text="EXECUTED LOI SELLER TRANSACTIONS YTD"
                    number={excecutedDashboard?.executedlo_seller}
                    className="flex-1"
                    onClick={() => handleBoxClick("executedlo_seller")}
                  />
                </div>


                <div className="flex gap-3 items-center w-[75%] ">
                  <CircleChart
                    Title="DAYS SINCE EXECUTED LOI"
                    TotalTitle="Total Excecuted LOI"
                    Total={excecutedDashboard?.total_executedloi}
                    data={data} />

                  <BarData
                    className="w-[50%]"
                    Title="MONTHLY GAIN IN POTENTIAL GCI"
                    BarGraphData={BarGraphData}
                    onBarClick={(key) => handleBoxClick(key)}
                  />
                </div>
              </div>

            </div>

          </div>

        </>
      )}

      <ExecutedAddModal showModal={showAddModal} onClose={() => setShowAddModal(false)} onCallApi={onSuccess} />

      <EditExecutedModal
        showModal={showEditModal}
        onClose={() => setShowEditModal(false)}
        id={selectedId}
        onCallApi={() => {
          setSelectedId("");
          onSuccess();
        }}

      />

      <ContractModal
        showModal={showContractModal}
        onClose={() => setShowContractModal(false)}
        id={selectedId}
        handleUnderContract={handleUnderContract}
      />

      <ConfirmationModal
        showModal={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        handleConfirm={handleDelete}
      />

      <DeleteConfirmationModal
        showModal={showDeleteModal}
        disable={disableDelete}
        onClose={() => setShowDeleteModal(false)}
        handleDelete={handleDelete}
      />
    </BaseLayout>
  );
};

export default ExecutedLOI;